// Core
import React, { Component } from 'react'
import { connect } from 'react-redux'
import css from './styles.scss'
import globalCss from '../../../scss/global.scss'
import {
  clearDeletedVariables,
  deleteVariable,
  stopEditingVariable,
  updateVariable,
  cloneVariable,
  generateGetter,
  updateVariableMacro,
  editingInstance
} from '../../actions/actions'
import { cloneDeep } from 'lodash'
import { generateVariableKey } from '../../utils'

import ArrayEditor from './ArrayEditor'
import FormulaEditor from './FormulaEditor'
import OperatorEditor from './OperatorEditor'
import ChoiceFromAnonymousEditor from './ChoiceFromAnonymousEditor'
import ChoiceFromReferenceEditor from './ChoiceFromReferenceEditor'
import ChoiceFromDataSourceEditor from './ChoiceFromDataSourceEditor'
import ChoiceFromDataSourceColumnEditor from './ChoiceFromDataSourceColumnEditor'
import ChoiceFromDataSourceColumnNameEditor from './ChoiceFromDataSourceColumnNameEditor'
import JavascriptEditor from './JavascriptEditor'
import GetterEditor from './GetterEditor'
import NumberEditor from './NumberEditor'
import ObjectEditor from './ObjectEditor'
import RandomNumberEditor from './RandomNumberEditor'
import ReplaceEditor from './ReplaceEditor'
import RoundEditor from './RoundEditor'
import SampleFromAnonymousEditor from './SampleFromAnonymousEditor'
import SampleFromReferenceEditor from './SampleFromReferenceEditor'
import StringEditor from './StringEditor'
import ToolTip from '../ToolTip'
import MathEditor from './MathEditor'
import BlankEditor from './BlankEditor'

export class EditVariable extends Component {
  constructor () {
    super()

    this.state = {
      report: false,
      name: '',
      isEditName: false,
      key: '',
      args: []
    }
    this.macroNames = {
      formula: 'Formula',
      operator: 'Operator',
      string: 'Text',
      number: 'Number',
      array: 'List',
      object: 'Mapping',
      getter: 'Getter',
      randomNumber: 'Random Number',
      choiceFromDataSource: 'Random Row',
      choiceFromDataSourceColumn: 'Random Cell',
      choiceFromDataSourceColumnName: 'Random Column Name',
      choiceFromAnonymous: 'Random Choice',
      choiceFromReference: 'Random Choice from Variable',
      sampleFromAnonymous: 'Random Sample',
      sampleFromReference: 'Random Sample from Variable',
      round: 'Round',
      replace: 'Find and Replace',
      javascript: 'Code',
      math: 'Math Expression'
    }
    this.macroHelp = {
      array: 'Enter pieces of text into the fields below. Retrieve one or more of these at random using the Random Choice/Sample from Variable types or at a specific position using the Getter type.',
      object: 'Enter key/value pairs in the fields below. Keys cannot change once entered. Retrieve keys from this mapping at random using the Random Choice/Sample from Variable types. You can then retrieve values at a specific key using the Getter type.',
      getter: 'Select a list or mapping variable and enter an index or key of a value to retrieve from it. Lists are indexed starting at 0. Therefore, to retrieve the first item in a list, you would enter 0 in the field below. For mappings, enter the key of the value you wish to retrieve. If the index or key is stored in another variable, enter the variable name and select “Key or index is a variable name”',
      randomNumber: 'Enter minimum and maximum values to generate a random integer between. Both the minimum and maximum values will be included as possible selections. Only one number will be selected no matter how many times you use the variable throughout your item.',
      choiceFromDataSource: 'Select a Data Source and an optional column to pull an element at random from. Elements will only be chosen once.',
      choiceFromDataSourceColumn: 'Select a Data Source and an optional column to pull an element at random from. Elements will only be chosen once.',
      choiceFromDataSourceColumnName: 'Select a Data Source to pull a column name at random. Elements will only be chosen once.',
      choiceFromAnonymous: 'Enter pieces of text into the fields below and one will be selected at random. Only one value will be chosen no matter how many times you use the variable throughout your item.',
      choiceFromReference: 'Select a list or mapping variable and a single selection will be made from it at random. For lists, the value will be selected. For mappings, the key will be selected. You can then access the mapping value using a getter variable. Only one selection will be made no matter how many times you use the variable throughout your item. If you have already made a random selection from this list/mapping and want to avoid previous selections, check the box to “Ignore previous selections”',
      sampleFromAnonymous: 'Enter pieces of text into the fields below and specify how many selections to make from it at random. The selections will be put into a list that you can interact with by using the Getter type.',
      sampleFromReference: 'Select a list or mapping variable and specify how many selections to make from it at random. For list variables, it will select values. For mappings, it will select keys. It will then put these selections into a list that you can interact with by using the Getter type. If you have already made a random selection from this list/mapping and want to avoid previous selections, check the box to “Ignore previous selections”',
      replace: 'Choose a value to replace inside a referenced variable.',
      round: 'Choose a variable to round. Choose a rounding scheme. You can round to decimal points or number intervals.',
      formula: 'Enter a value or variable and then select the desired operator. To randomly select an operator click the question mark followed by any operators you want. To randomly select from all operators click the question mark only.',
      operator: 'Choose a formula and a step to pull an operator from.',
      javascript: <span>Write JavaScript code in the field below. You have access to all previously defined variables. It uses an “implicit return”, meaning the last line will be the value assigned to this variable. For example, if you have two random numbers, a and b, and you want to assign their product to this variable, simply type “a * b”, and their product will be returned. For your convenience, you have access to lodash helper functions, which are documented <a href='https://lodash.com/docs/' target='_blank' rel='noopener noreferrer'>here</a>.</span>,
      math: <span>Write complex math expressions in the field below. Some examples of possible expressions include <code>sqrt(3^2 + 4^2)</code>, <code>2 inch to cm</code> and <code>cos(45 deg)</code>. You can include variables in an expression using the following syntax <code>{'{{variable name}}'}</code>. For additional help, click <a href="https://mathjs.org/examples/index.html" target="blank">here.</a></span>
    }
    this.referenceFriendly = [
      'array',
      'object',
      'sampleFromAnonymous',
      'sampleFromReference',
      'choiceFromDataSource'
    ]
    this.controller = AbortController ? new AbortController() : {}
  }

  componentDidMount () {
    document.addEventListener('click', this.closeEditName, { capture: true })
  }

  componentWillUnmount () {
    this.controller.abort()
    document.removeEventListener('click', this.closeEditName, { capture: true })
    window.removeEventListener('keyup', this.keyedUp)
  }

  closeEditName = evt => {
    const { isEditName } = this.state
    if (isEditName && !evt.target.hasAttribute(['data-notoggle'])) {
      this.handleEditName()
    }
  }

  handleBgPress = evt => {
    if (evt.target === evt.currentTarget) {
      this.handleStopEditing()
    }
  }

  save = evt => {
    this.setState({
      report: true
    }, () => {
      this.setState({
        report: false
      })
    })
  }

  generateGetter = (reference, source) => {
    const variableName = `${source}Value`
    const { item: { variables } } = this.props
    if (variables.hasOwnProperty(variableName)) { // eslint-disable-line no-prototype-builtins
      this.props.dispatch(updateVariable(variableName, [reference, source, true]))
    } else {
      this.props.dispatch(generateGetter(variableName, [reference, source, true]))
    }
  }

  generateDataSourceGetter = (reference, source, isReference) => {
    const { item: { variables } } = this.props
    const variableName = variables[reference].name + ' - ' + source
    const keyName = generateVariableKey()
    this.props.dispatch(generateGetter(keyName, variableName, [reference, source, isReference]))
  }

  handleDeleteVar = () => {
    const varName = this.props.variables.editing

    if (!confirm(`Confirm delete variable ${this.props.item.varNameMap[varName] || varName}?`)) return

    this.handleStopEditing(true)
    this.props.dispatch(deleteVariable(varName))
    setTimeout(() => {
      this.props.dispatch(clearDeletedVariables())
    }, 0)
  }

  handleStopEditing = (backToItem = false) => {
    window.removeEventListener('keyup', this.keyedUp)
    this.props.dispatch(stopEditingVariable())
    this.props.dispatch(editingInstance())
    if (backToItem) {
      this.props.onClearModal()
    }
  }

  updateVariableData = (varKey, args) => {
    const state = {}

    if (args) {
        state.args = args
    }

    this.setState(state, () => {
        this.props.dispatch(updateVariable(this.state.key, this.state.name, this.state.args))
    })
  }

  listenForEsc () {
    window.removeEventListener('keyup', this.keyedUp)
    window.addEventListener('keyup', this.keyedUp)
  }

  keyedUp = evt => {
    if (evt.keyCode === 27) {
      this.handleStopEditing(true)
    }
  }

  isVariableNameUnique (name) {
    const { item: { varNameMap } } = this.props
    if (Object.values(varNameMap).indexOf(name) > -1) {
      return false
    }
    return true
  }

  generateCloneName (oldName) {
    const split = oldName.split(' ')
    let num = split[split.length - 1]
    let nameBase, newName
    // Check if it was numbered already or not
    if (!num || isNaN(num)) {
      num = 1
      nameBase = split.join(' ')
    } else {
      num++
      nameBase = split.slice(0, -1).join(' ')
    }
    // Loop until a unique name is found
    while (true) {
      newName = nameBase + ' ' + num
      if (this.isVariableNameUnique(newName)) {
        break
      }
      num++
    }
    return newName
  }

  handleClonedVariable = () => {
    const { item: { variables, varNameMap }, variables: { editing } } = this.props
    const clonedVariable = cloneDeep(variables[editing])
    const name = this.generateCloneName(varNameMap[editing])
    clonedVariable.name = name
    const keyName = generateVariableKey()
    this.props.dispatch(cloneVariable(keyName, name, clonedVariable))
  }

  componentWillReceiveProps (nextProps) {
    const { variables: { instance, editing, editing: nextVarEditing }, item: { variables, variables: nextVars, varNameMap } } = nextProps
    const { variables: { editing: varEditing }, modal } = this.props
    const nextVariable = nextVars ? nextVars[nextVarEditing] : false

    if (nextVarEditing && nextVariable && nextVarEditing !== varEditing) {
      this.listenForEsc()
    }
    if (modal === 'edit' && nextProps.modal !== 'edit') {
      this.handleStopEditing()
    }
    if (!nextVarEditing || !variables || !variables[nextVarEditing]) {
      return
    }
    let hasInstance = false
    if (variables[nextVarEditing].instances) {
      hasInstance = true
    }
    this.setState({
      modifiers: variables[nextVarEditing].modifiers || [],
      instanceModifiers: hasInstance ? variables[nextVarEditing].instances[instance] : []
    })
    if (varEditing === nextVarEditing) {
      return
    }
    if (editing) {
      this.props.onHandleModal(null, true)
    }
    if (varEditing && instance) {
      this.props.dispatch(editingInstance())
    }
    this.setState({
      name: varNameMap[nextVarEditing],
      key: nextVarEditing,
      args: variables[nextVarEditing].args,
      isEditName: false
    }, () => {
      if (varNameMap[nextVarEditing].includes('unnamed')) {
        this.handleEditName()
      }
    })
  }

  updateMacro = (varName, macro) => {
    this.props.dispatch(updateVariableMacro(varName, macro))
  }

  handleUpdateName = event => {
    this.setState({
      name: event.target.value
    })
  }

  handleEnter = evt => {
    if (evt.keyCode === 13) {
      this.handleEditName()
    }
  }

  handleEditName = () => {
    this.setState(prevState => {
      return {
        isEditName: !prevState.isEditName
      }
    }, () => {
      if (this.state.isEditName) {
        const input = document.getElementById('name-editor')
        input.focus()
        input.select()
      } else {
        this.updateVariableData()
      }
    })
  }

  render () {
    const { variables: { instance, editing: variableName }, item: { variables: vars, variableOrder: order, dataSources } } = this.props
    const { app: { primaryColor } } = this.props
    const { report, modifiers, instanceModifiers } = this.state
    const variables = vars || {} // since null is a valid argument
    const variable = variables[variableName]

    if (!variableName || !variable) {
      return null
    }

    const helpText = this.macroHelp[variable.macro]
    let helpContent = null
    const referenceVariables = []
    if (helpText) {
      helpContent = <ToolTip tips={helpText} minWidth={400} />
    }
    order.map(key => {
      if (this.referenceFriendly.indexOf(variables[key].macro) > -1) {
        referenceVariables.push(key)
      }
    })
    const macroProps = {
      report,
      variable,
      variableName,
      referenceVariables,
      vars,
      key: variableName,
      update: this.save,
      save: this.updateVariableData,
      generateGetter: this.generateGetter,
      generateDataSourceGetter: this.generateDataSourceGetter,
      updateType: this.updateType,
      updateMacro: this.updateMacro,
      dataSources: dataSources,
      instance,
      instanceModifiers,
      modifiers,
      primaryColor
    }
    let macroEditor = null

    switch (variable.macro) {
      case 'choiceFromDataSource':
        if (!variable.args[0]) {
          for (const dataSourceId in dataSources) { // eslint-disable-line no-unused-vars
            if (dataSources.hasOwnProperty(dataSourceId)) { // eslint-disable-line no-prototype-builtins
              macroProps.variable.args[0] = dataSourceId
              break
            }
          }
        }
        macroEditor = <ChoiceFromDataSourceEditor {...macroProps} />
        break
      case 'choiceFromDataSourceColumn':
        if (!variable.args[0]) {
          for (const dataSourceId in dataSources) { // eslint-disable-line no-unused-vars
            if (dataSources.hasOwnProperty(dataSourceId)) { // eslint-disable-line no-prototype-builtins
              macroProps.variable.args[0] = dataSourceId
              break
            }
          }
        }
        macroEditor = <ChoiceFromDataSourceColumnEditor {...macroProps} />
        break
      case 'choiceFromDataSourceColumnName':
        macroEditor = <ChoiceFromDataSourceColumnNameEditor {...macroProps} />
        break
      case 'choiceFromReference':
        macroEditor = <ChoiceFromReferenceEditor {...macroProps} />
        break
      case 'choiceFromAnonymous':
        macroEditor = <ChoiceFromAnonymousEditor {...macroProps} />
        break
      case 'sampleFromReference':
        macroEditor = <SampleFromReferenceEditor {...macroProps} />
        break
      case 'sampleFromAnonymous':
        macroEditor = <SampleFromAnonymousEditor {...macroProps} />
        break
      case 'randomNumber':
        macroEditor = <RandomNumberEditor {...macroProps} />
        break
      case 'number':
        macroEditor = <NumberEditor {...macroProps} />
        break
      case 'string':
        macroEditor = <StringEditor {...macroProps} />
        break
      case 'array':
        macroEditor = <ArrayEditor {...macroProps} />
        break
      case 'object':
        macroEditor = <ObjectEditor {...macroProps} />
        break
      case 'javascript':
        macroEditor = <JavascriptEditor {...macroProps} />
        break
      case 'getter':
        macroEditor = <GetterEditor {...macroProps} />
        break
      case 'formula':
        macroEditor = <FormulaEditor {...macroProps} />
        break
      case 'operator':
        macroEditor = <OperatorEditor {...macroProps} />
        break
      case 'round':
        macroEditor = <RoundEditor {...macroProps} />
        break
      case 'replace':
        macroEditor = <ReplaceEditor {...macroProps} />
        break
      case 'math':
        macroEditor = <MathEditor {...macroProps} />
        break
      case 'blank':
        macroEditor = <BlankEditor {...macroProps} />
        break
      default:
        macroEditor = (<p>Variable type not found</p>)
        break
    }
    let nameEditor
    if (this.state.isEditName) {
      nameEditor = (
        <input
          className={css.input}
          type='text'
          value={this.state.name}
          onChange={this.handleUpdateName}
          onKeyUp={this.handleEnter}
          data-notoggle
          id='name-editor'
        />
      )
    } else {
      nameEditor = (
        this.state.name
      )
    }
    let cloneBtn
    // Show clone button if not on the Getter editor
    if (variable.macro !== 'getter') {
      cloneBtn = (
        <button type='button' onClick={this.handleClonedVariable} className={css.btn}>
        Clone
        </button>
      )
    }
    return (
      <article className={css.backer} onClick={this.handleBgPress}>
        <div className={`${css.frame} ${this.props.app.standAloneMode ? css.frameStandAlone : ''}`}>
          <header className={css.header}>
            <h3 className={css.h3}>
              <div className={css.editWrapper}>{nameEditor} &nbsp;
                <span className={css.edit}><i className={globalCss.iconEditCircle} onClick={this.handleEditName} data-notoggle title='Edit Name' /></span>
              </div>
            </h3>
            <h4 className={css.h4}>
              <span>{this.macroNames[variable.macro]}</span>
              <span>{instance ? ' - Instance' : null}</span>
              <span>{helpContent}</span>
            </h4>
          </header>
          <section className={css.content}>
            {macroEditor}
          </section>
        </div>
        <div className={css.bottomOptions}>
          {cloneBtn}
          <button type='button' onClick={this.handleDeleteVar} className={css.btn}>
              Delete
          </button>
        </div>
      </article>
    )
  }
}

const mapStateToProps = state => {
  const { app, auth, item, variables } = state
  return {
    app,
    auth,
    item,
    variables
  }
}

export default connect(mapStateToProps)(EditVariable)



// WEBPACK FOOTER //
// /app/js/components/EditVariable/index.js